import ExpressionResultModel from 'stores/models/ExpressionResult';
import { StatusType, StatusTypes } from 'stores/models/Gene';
import { IOperon } from 'stores/models/LacOperon';
import TestTubeRack from 'stores/models/TestTubeRack';
import { GeneType, GeneTypes, ParsedOperon } from 'stores/models/types';

export const parseForTubes = (result: ExpressionResultModel) => {
  const hasIPTGz = !!result.output?.includes('Z');
  const hasIPTGa = !!result.output?.includes('A');
  const hasONPGz = !!(hasIPTGz && result.lacZ === StatusTypes.Constitutive);
  const hasONPGa = !!(hasIPTGa && result.lacA === StatusTypes.Constitutive);

  return new TestTubeRack(hasIPTGz, hasIPTGa, hasONPGz, hasONPGa);
};

const parseOperon = (operon?: IOperon): ParsedOperon => {
  const output = operon?.expressedOutput.get();
  const lacZ = operon?.getGeneStatus(GeneTypes.LacZ);
  const lacA = operon?.getGeneStatus(GeneTypes.LacA);

  return {
    operon,
    output,
    lacZ,
    lacA,
  };
};

export const parseOperons = (model: any) => {
  const parsedBacterial = parseOperon(model?.bacterialDNA);
  const parsedPlasmid = parseOperon(model?.plasmidDNA);

  const isComplementationTest = !!parsedPlasmid.operon;
  const { lacZ, lacA, output } = isComplementationTest
    ? testBoth(parsedBacterial, parsedPlasmid)
    : testSingle(parsedBacterial);

  const result = {
    bacterial: parsedBacterial,
    plasmid: parsedPlasmid,
    lacZ,
    lacA,
    output,
  };

  return result;
};

const getDominantStatus = (
  statuses: (StatusType | null | undefined)[]
): StatusType => {
  if (statuses.find(statusFinder(StatusTypes.Constitutive)))
    return StatusTypes.Constitutive;

  if (statuses.find(statusFinder(StatusTypes.Inducible)))
    return StatusTypes.Inducible;

  return StatusTypes.NonInducible;
};

const getInTransAwareStatus = (
  cisTarget: IOperon | undefined,
  transTarget: IOperon | undefined,
  geneType: GeneType
) => {
  const hasCisInducibleRepressor =
    cisTarget?.getGeneStatus(GeneTypes.LacI) === StatusTypes.Inducible;
  const hasTransInducibleRepressor =
    transTarget?.getGeneStatus(GeneTypes.LacI) === StatusTypes.Inducible;
  const hasCisDominant =
    cisTarget?.getGeneStatus(GeneTypes.LacO) === StatusTypes.Constitutive;
  const hasTransDominant =
    !hasCisDominant && !hasCisInducibleRepressor && hasTransInducibleRepressor;

  return cisTarget?.getGeneStatus(geneType, hasTransDominant);
};

const getStatuses = (
  bacterial: IOperon | undefined,
  wasBacterialRepressed: boolean,
  plasmid: IOperon | undefined,
  wasPlasmidRepressed: boolean,
  output: string,
  type: GeneType
) => {
  const bacterialBaseLacFunctional = getInTransAwareStatus(
    bacterial,
    plasmid,
    type
  );
  const plasmidBaseLacFunctional = getInTransAwareStatus(
    plasmid,
    bacterial,
    type
  );

  return output
    ? [
        wasBacterialRepressed
          ? StatusTypes.NonInducible
          : bacterialBaseLacFunctional,
        wasPlasmidRepressed
          ? StatusTypes.NonInducible
          : plasmidBaseLacFunctional,
      ]
    : [];
};

const statusFinder = (type: StatusType) => (status?: StatusType | null) =>
  status === type;

const testSingle = ({ lacZ, lacA, output }: ParsedOperon) => {
  return { lacZ, lacA, output };
};

const testBoth = (
  parsedBacterial: ParsedOperon,
  parsedPlasmid: ParsedOperon
) => {
  const bacterial = parsedBacterial.operon;
  const plasmid = parsedPlasmid.operon;

  console.log('Test --------------------------------------------------------');

  console.log('bacterial', bacterial?.expressedOutput.get());
  console.log('plasmid', plasmid?.expressedOutput.get());

  const hasSuperRepressor =
    bacterial?.isSuperRepressed.get() || plasmid?.isSuperRepressed.get();
  console.log('hasSuperRepressor:', hasSuperRepressor);

  let lacZ,
    lacA,
    output,
    wasBacterialRepressed = false,
    wasPlasmidRepressed = false,
    tempBacterialExpressed = '',
    tempPlasmidExpressed = '';
  const isBacterialDominant = bacterial?.isConstitutiveDominant.get();
  const isPlasmidDominant = plasmid?.isConstitutiveDominant.get();

  console.log('isBacterialDominant:', isBacterialDominant);
  console.log('isPlasmidDominant:', isPlasmidDominant);

  // Both constitutive
  if (isBacterialDominant && isPlasmidDominant) {
    console.log('culture status: both', StatusTypes.Constitutive);
    tempBacterialExpressed = parsedBacterial.output || '';
    tempPlasmidExpressed = parsedPlasmid.output || '';
  }
  // Either constitutive
  else if (isBacterialDominant || isPlasmidDominant) {
    if (hasSuperRepressor) {
      console.log('isBacterialDominant', isBacterialDominant);
      console.log('isPlasmidDominant', isPlasmidDominant);

      // Bacterial gets repressed
      if (isBacterialDominant) {
        tempBacterialExpressed = parsedBacterial.output || '';
      } else {
        tempBacterialExpressed = '';
        wasBacterialRepressed = true;
      }

      // Plasmid gets repressed
      if (isPlasmidDominant) {
        tempPlasmidExpressed = parsedPlasmid.output || '';
      } else {
        tempPlasmidExpressed = '';
        wasPlasmidRepressed = true;
      }
    } else {
      tempBacterialExpressed = parsedBacterial.output || '';
      tempPlasmidExpressed = parsedPlasmid.output || '';
    }
  }
  // Neither constitutive
  else {
    if (hasSuperRepressor) {
      console.log(
        'Both are Non Inducible (were Inducible, but there is a presence of superrepressor in the cell)'
      );
      tempBacterialExpressed = '';
      tempPlasmidExpressed = '';
    } else {
      console.log('Both are Inducible');
      tempBacterialExpressed = parsedBacterial.output || '';
      tempPlasmidExpressed = parsedPlasmid.output || '';
    }
  }

  output = tempBacterialExpressed + tempPlasmidExpressed;
  output = Array.from(new Set(output.split('')))
    .toString()
    .replaceAll(',', '');

  const lacZs = getStatuses(
    bacterial,
    wasBacterialRepressed,
    plasmid,
    wasPlasmidRepressed,
    output,
    GeneTypes.LacZ
  );
  lacZ = getDominantStatus(lacZs);

  const lacAs = getStatuses(
    bacterial,
    wasBacterialRepressed,
    plasmid,
    wasPlasmidRepressed,
    output,
    GeneTypes.LacA
  );
  lacA = getDominantStatus(lacAs);

  console.log('lacZs:', lacZs);
  console.log('lacZ:', lacZ);
  console.log('lacAs:', lacAs);
  console.log('lacA:', lacA);
  console.log('output:', output);

  return {
    lacZ,
    lacA,
    output,
  };
};
